/**
 * Frontend mirror of product-role ranks used for “view as” demotion.
 * Keep aligned with artifacts/api-server/src/plugins/auth/capabilities.ts.
 */

export type ProductRoleCode =
  | 'employee'
  | 'team_assistant'
  | 'space_admin'
  | 'it_admin'
  | 'visitor_host';

export type AppRole = 'viewer' | 'editor' | 'admin';

export const PRODUCT_ROLE_RANK: Record<ProductRoleCode, number> = {
  employee: 10,
  visitor_host: 10,
  team_assistant: 20,
  space_admin: 30,
  it_admin: 40
};

export const PRODUCT_ROLE_LABELS: Record<ProductRoleCode, string> = {
  employee: 'Employee',
  visitor_host: 'Visitor host',
  team_assistant: 'Team assistant',
  space_admin: 'Space admin',
  it_admin: 'IT admin'
};

export const ALL_PRODUCT_ROLES: readonly ProductRoleCode[] = [
  'it_admin',
  'space_admin',
  'team_assistant',
  'visitor_host',
  'employee'
];

const ROLE_DEFAULT_CAPABILITIES: Record<ProductRoleCode, readonly string[]> = {
  employee: ['booking:self', 'ai:booking', 'visitors:host'],
  visitor_host: ['booking:self', 'ai:booking', 'visitors:host'],
  team_assistant: [
    'booking:self',
    'booking:on_behalf',
    'ai:booking',
    'visitors:host'
  ],
  space_admin: [
    'booking:self',
    'booking:on_behalf',
    'booking:force_cancel',
    'inventory:manage',
    'policy:manage',
    'reports:view',
    'ai:booking',
    'ai:admin_insights',
    'visitors:host',
    'visitors:desk',
    'visitors:admin'
  ],
  it_admin: [
    'booking:self',
    'booking:on_behalf',
    'booking:force_cancel',
    'inventory:manage',
    'policy:manage',
    'reports:view',
    'audit:view',
    'integrations:manage',
    'ai:booking',
    'ai:admin_insights',
    'visitors:host',
    'visitors:desk',
    'visitors:admin'
  ]
};

/** Plain-language descriptions of each capability a role can grant. */
const CAPABILITY_DESCRIPTIONS: Record<string, string> = {
  'booking:self': 'Book desks and spaces for themselves',
  'booking:on_behalf': 'Book desks and spaces for other people',
  'booking:force_cancel': "Cancel other people's bookings",
  'inventory:manage': 'Manage floors, desks, rooms, parking and maps',
  'policy:manage': 'Manage booking rules, limits and amenity rules',
  'reports:view': 'View reports on how spaces are used',
  'audit:view': 'See the audit log of who changed what, and when',
  'integrations:manage': 'Manage connections to outside systems such as Microsoft 365',
  'ai:booking': 'Use AI booking help',
  'ai:admin_insights': 'See AI summaries for administrators',
  'visitors:host': 'Register their own visitors in advance',
  'visitors:desk': "Check in and check out anyone's visitors at the front desk",
  'visitors:admin': 'See the full visitor log and export it'
};

/**
 * What each role can do, in plain language, derived from the role's default
 * capabilities so the guide stays aligned with the permissions it grants.
 */
export function describeRoleAbilities(role: ProductRoleCode): string[] {
  return ROLE_DEFAULT_CAPABILITIES[role].map(
    (capability) => CAPABILITY_DESCRIPTIONS[capability] ?? capability
  );
}

export function isProductRoleCode(value: string): value is ProductRoleCode {
  return (
    value === 'employee' ||
    value === 'team_assistant' ||
    value === 'space_admin' ||
    value === 'it_admin' ||
    value === 'visitor_host'
  );
}

export function maxProductRoleRank(roles: readonly string[]): number {
  let max = 0;
  for (const role of roles) {
    if (isProductRoleCode(role)) {
      max = Math.max(max, PRODUCT_ROLE_RANK[role]);
    }
  }
  return max;
}

export function listViewAsRoles(assigned: readonly string[]): ProductRoleCode[] {
  const max = maxProductRoleRank(assigned);
  if (max < PRODUCT_ROLE_RANK.space_admin) return [];
  return ALL_PRODUCT_ROLES.filter((role) => PRODUCT_ROLE_RANK[role] < max);
}

export function canViewAsRole(assigned: readonly string[], target: string): boolean {
  return isProductRoleCode(target) && listViewAsRoles(assigned).includes(target);
}

export function appRoleFromProductRoles(roles: readonly ProductRoleCode[]): AppRole {
  if (roles.some((r) => r === 'it_admin' || r === 'space_admin')) return 'admin';
  if (
    roles.some(
      (r) => r === 'employee' || r === 'team_assistant' || r === 'visitor_host'
    )
  ) {
    return 'editor';
  }
  return 'viewer';
}

export function demoteToProductRole(target: ProductRoleCode): {
  role: AppRole;
  productRoles: ProductRoleCode[];
  capabilities: string[];
} {
  return {
    role: appRoleFromProductRoles([target]),
    productRoles: [target],
    capabilities: [...ROLE_DEFAULT_CAPABILITIES[target]].sort()
  };
}

/** True when the principal may open the profile role-switcher. */
export function canUseRoleSwitcher(assigned: readonly string[]): boolean {
  return listViewAsRoles(assigned).length > 0;
}
